import styled, { keyframes } from 'styled-components'

const INK = '#1b1230'
const PAPER = '#f9f8fc'
const PURPLE = '#6d28d9'
const PURPLE_SOFT = '#efe9fe'
const EMERALD = '#0e9f6e'
const EMERALD_SOFT = '#dff5ec'
const MUTED = '#6b6479'
const LINE = '#e7e3f0'
const DISPLAY = 'var(--font-display)'

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`

const stampIn = keyframes`
  0% { opacity: 0; transform: rotate(-8deg) scale(1.8); }
  55% { opacity: 1; transform: rotate(-8deg) scale(0.92); }
  100% { opacity: 1; transform: rotate(-8deg) scale(1); }
`

const Page = styled.div`
  width: 100%;
  color: ${INK};
  overflow-x: hidden;
`

const Container = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 28px;
`

/* ---------- Hero ---------- */

const Hero = styled.section`
  position: relative;
  padding: 40px 0 96px;
`

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 56px;
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 44px;
  }
`

const HeroCopy = styled.div`
  padding-top: 24px;
`

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${PURPLE};
  margin-bottom: 22px;
  animation: ${fadeUp} 0.6s ease both;

  &::before {
    content: '';
    width: 22px;
    height: 2px;
    background: ${PURPLE};
  }
`

const HeroTitle = styled.h1`
  font-family: ${DISPLAY};
  font-size: clamp(44px, 6.4vw, 78px);
  line-height: 0.98;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0 0 24px;
  animation: ${fadeUp} 0.6s ease 0.06s both;
`

const HeroLead = styled.p`
  font-size: 19px;
  line-height: 1.6;
  color: ${MUTED};
  max-width: 500px;
  margin: 0 0 32px;
  animation: ${fadeUp} 0.6s ease 0.12s both;

  b {
    color: ${INK};
    font-weight: 600;
  }
`

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
  animation: ${fadeUp} 0.6s ease 0.18s both;
`

const PrimaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${PURPLE};
  color: #ffffff;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  padding: 15px 28px;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(109, 40, 217, 0.24);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(109, 40, 217, 0.3);
  }
`

const TrustLine = styled.span`
  font-size: 14px;
  color: ${MUTED};
`

/* ---------- Hero artifact (the signature) ---------- */

const ArtifactStage = styled.div`
  position: relative;
  animation: ${fadeUp} 0.7s ease 0.1s both;
`

const ArtifactCard = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid ${LINE};
  border-radius: 20px;
  padding: 26px 26px 22px;
  box-shadow:
    0 30px 60px -24px rgba(27, 18, 48, 0.28),
    0 8px 20px -12px rgba(27, 18, 48, 0.16);
  transform: rotate(1.4deg);
  max-width: 380px;
  margin-left: auto;
`

const ArtHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`

const ArtLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: contain;
  background: #ffffff;
  border: 1px solid ${LINE};
  flex-shrink: 0;
`

const ArtCompany = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 1.15;
  color: ${INK};
`

const ArtTag = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${PURPLE};
  background: ${PURPLE_SOFT};
  padding: 4px 9px;
  border-radius: 999px;
`

const ArtLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 11px 0;
  border-bottom: 1px solid #f1eef8;
  font-size: 14px;
  color: #40384f;

  span:last-child {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: ${INK};
  }
`

const ArtTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 16px;
  margin-bottom: 20px;

  span:first-child {
    font-size: 13px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${MUTED};
  }

  span:last-child {
    font-family: ${DISPLAY};
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: ${INK};
    font-variant-numeric: tabular-nums;
  }
`

const ArtApprove = styled.div`
  text-align: center;
  background: ${EMERALD};
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  padding: 12px;
  border-radius: 11px;
`

const ViewedChip = styled.div`
  position: absolute;
  z-index: 3;
  top: -16px;
  left: -28px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid ${LINE};
  border-radius: 999px;
  padding: 8px 14px 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: ${INK};
  box-shadow: 0 12px 26px -14px rgba(27, 18, 48, 0.35);

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${EMERALD};
    box-shadow: 0 0 0 4px ${EMERALD_SOFT};
  }

  @media (max-width: 860px) {
    left: 0;
  }
`

const ApprovedStamp = styled.div`
  position: absolute;
  bottom: 8px;
  right: -20px;
  transform: rotate(-8deg);
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: 22px;
  letter-spacing: 0.06em;
  color: ${EMERALD};
  border: 3px solid ${EMERALD};
  border-radius: 10px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 0 0 2px rgba(14, 159, 110, 0.25);
  animation: ${stampIn} 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.2) 0.85s both;

  @media (max-width: 860px) {
    right: 4px;
  }
`

/* ---------- Thesis ---------- */

const Thesis = styled.section`
  border-top: 1px solid ${LINE};
  border-bottom: 1px solid ${LINE};
  padding: 72px 0;
`

const ThesisLine = styled.p`
  font-family: ${DISPLAY};
  font-size: clamp(30px, 4.4vw, 52px);
  line-height: 1.06;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  max-width: 900px;

  em {
    font-style: normal;
    color: ${PURPLE};
  }
`

const ThesisSub = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: ${MUTED};
  margin: 22px 0 0;
  max-width: 620px;
`

/* ---------- Substance ---------- */

const Substance = styled.section`
  padding: 84px 0 40px;
`

const SectionEyebrow = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${PURPLE};
  margin-bottom: 40px;
`

const PointRow = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 40px;
  padding: 40px 0;
  border-top: 1px solid ${LINE};

  &:last-child {
    border-bottom: 1px solid ${LINE};
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 32px 0;
  }
`

const PointTitle = styled.h2`
  font-family: ${DISPLAY};
  font-size: clamp(24px, 2.8vw, 32px);
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
`

const PointBody = styled.p`
  font-size: 18px;
  line-height: 1.65;
  color: ${MUTED};
  margin: 0;
  max-width: 520px;
`

/* ---------- Practical ---------- */

const Practical = styled.section`
  padding: 60px 0 84px;
`

const PracticalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const PracticalItemBox = styled.div`
  padding-top: 18px;
  border-top: 2px solid ${INK};
`

const PracticalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
  color: ${INK};
`

const PracticalBody = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: ${MUTED};
  margin: 0;
`

/* ---------- Steps ---------- */

const Steps = styled.section`
  padding: 40px 0 96px;
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 36px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`

const StepBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StepNumber = styled.div`
  font-family: ${DISPLAY};
  font-size: 15px;
  font-weight: 700;
  color: ${PURPLE};
  letter-spacing: 0.08em;
`

const StepTitle = styled.h3`
  font-family: ${DISPLAY};
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
`

const StepBody = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${MUTED};
  margin: 0;
`

/* ---------- Closing CTA ---------- */

const Cta = styled.section`
  background: ${INK};
  color: ${PAPER};
  border-radius: 28px;
  margin: 0 0 64px;
  padding: 72px 56px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    right: -120px;
    top: -120px;
    width: 340px;
    height: 340px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(109, 40, 217, 0.55),
      transparent 68%
    );
  }

  @media (max-width: 640px) {
    padding: 52px 28px;
    border-radius: 22px;
  }
`

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 620px;
`

const CtaHeading = styled.h2`
  font-family: ${DISPLAY};
  font-size: clamp(28px, 3.6vw, 42px);
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 16px;
`

const CtaBody = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #c9c2dd;
  margin: 0 0 30px;
`

const CtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  color: ${INK};
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  padding: 15px 28px;
  border-radius: 12px;
`

/* ---------- Footer ---------- */

const Footer = styled.footer`
  border-top: 1px solid ${LINE};
  padding: 30px 0 48px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const FooterBrand = styled.div`
  font-family: ${DISPLAY};
  font-size: 18px;
  font-weight: 800;
  color: ${INK};
`

const FooterMeta = styled.div`
  font-size: 14px;
  color: ${MUTED};
`

const FooterLink = styled.a`
  color: ${PURPLE};
  text-decoration: none;
  font-weight: 600;
`

export {
  Page,
  Container,
  Hero,
  HeroGrid,
  HeroCopy,
  Eyebrow,
  HeroTitle,
  HeroLead,
  CtaRow,
  PrimaryLink,
  TrustLine,
  ArtifactStage,
  ArtifactCard,
  ArtHeader,
  ArtLogo,
  ArtCompany,
  ArtTag,
  ArtLine,
  ArtTotal,
  ArtApprove,
  ViewedChip,
  ApprovedStamp,
  Thesis,
  ThesisLine,
  ThesisSub,
  Substance,
  SectionEyebrow,
  PointRow,
  PointTitle,
  PointBody,
  Practical,
  PracticalGrid,
  PracticalItemBox,
  PracticalTitle,
  PracticalBody,
  Steps,
  StepsGrid,
  StepBox,
  StepNumber,
  StepTitle,
  StepBody,
  Cta,
  CtaInner,
  CtaHeading,
  CtaBody,
  CtaLink,
  Footer,
  FooterBrand,
  FooterMeta,
  FooterLink,
}
